import React, { useState, useEffect, useRef } from 'react';

import { IconMap } from '../IconMap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { services } from '../mockData';
import QuoteForm from './QuoteForm/QuoteForm';

const { Menu, X, ChevronDown } = IconMap;


const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isQuoteFormOpen, setIsQuoteFormOpen] = useState(false);

  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // ⚡ Optimized Scroll Listener (prevents forced reflow)
  useEffect(() => {
    let lastY = 0;
    const handleScroll = () => {
      const y = window.scrollY;
      if (Math.abs(y - lastY) > 5) {
        setIsScrolled(y > 20);
        lastY = y;
      }
    };

    const throttled = () => requestAnimationFrame(handleScroll);
    window.addEventListener('scroll', throttled);

    return () => window.removeEventListener('scroll', throttled);
  }, []);

  // Click outside dropdown
  useEffect(() => {
    const closeDropdown = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", closeDropdown);
    return () => document.removeEventListener("mousedown", closeDropdown);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
      setIsServicesOpen(false);
    }
  };

  const handleContactClick = () => {
    if (location.pathname === '/') {
      scrollToSection('contact');
    } else {
      navigate('/contact');
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 will-change-transform ${isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
          }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">

            {/* Optimized Logo with width/height = Stops CLS */}
            <Link to="/" className="flex items-center cursor-pointer">
              <img
                src="/img/mbw_1.png"
                //src="https://customer-assets.emergentagent.com/job_fad851ea-a437-4828-91f9-49571711ae7a/artifacts/s6injtxh_mbw.png"   // 🔥 host locally (much faster)
                alt="MBW Techimpex"
                width="140"
                height="40"
                loading="eager"         // 🔥 improves LCP
                className="h-10 w-auto"
              />  
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-blue-600 font-medium">
                Home
              </Link>

              <Link to="/about" className="text-gray-700 hover:text-blue-600 font-medium">
                About Us
              </Link>

              {/* Services Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  className="text-gray-700 hover:text-blue-600 font-medium flex items-center gap-1"
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                >
                  Our Services <ChevronDown className="w-4 h-4" />
                </button>

                {isServicesOpen && (
                  <div
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[600px] bg-white rounded-lg shadow-xl border border-gray-100 p-6 will-change-transform"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      {services.map((service) => {
                        const IconComponent = IconMap[service.icon];
                        return (
                          <button
                            key={service.id}
                            onClick={() => {
                              navigate(`/${service.link}`);
                              setIsServicesOpen(false);
                            }}
                            className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
                          >
                            {IconComponent && (
                              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                                <IconComponent className="w-5 h-5 text-blue-600" />
                              </div>
                            )}
                            <div>
                              <h3 className="font-medium text-gray-900 text-sm">{service.title}</h3>
                              <p className="text-xs text-gray-600 line-clamp-2">{service.description}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <Link to="/portfolio" className="text-gray-700 hover:text-blue-600 font-medium">
                Portfolio
              </Link>

              <Link to="/blog" className="text-gray-700 hover:text-blue-600 font-medium">
                Blog
              </Link>

              <Link to="/contact" className="text-gray-700 hover:text-blue-600 font-medium">
                Contact
              </Link>
            </nav>

            {/* Quote Button */}
            <div className="hidden lg:block">
              <Button
                onClick={() => setIsQuoteFormOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium"
              >
                Get Quote
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-100 animate-fadeIn">
              <div className="flex flex-col space-y-3 items-start">
                <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-700 px-2 py-2 rounded-lg w-full text-left hover:bg-gray-50">
                  Home
                </Link>

                <Link to="/about" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-700 px-2 py-2 rounded-lg w-full text-left hover:bg-gray-50">
                  About Us
                </Link>

                <div className="w-full">
                  <button
                    onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                    className="flex items-center justify-between w-full text-gray-700 px-2 py-2 rounded-lg text-left hover:bg-gray-50"
                  >
                    <span>Our Services</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isMobileServicesOpen && (
                    <div className="pl-4 mt-1 space-y-1 border-l-2 border-gray-100 ml-2">
                      {services.map((service) => {
                        const IconComponent = IconMap[service.icon];
                        return (
                          <button
                            key={service.id}
                            onClick={() => {
                              navigate(`/${service.link}`);
                              setIsMobileMenuOpen(false);
                              setIsMobileServicesOpen(false);
                            }}
                            className="flex items-center gap-2 w-full p-2 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-gray-50 text-sm text-left"
                          >
                            {IconComponent && <IconComponent className="w-4 h-4" />}
                            <span>{service.title}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <Link to="/portfolio" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-700 px-2 py-2 rounded-lg w-full text-left hover:bg-gray-50">
                  Portfolio
                </Link>

                <Link to="/blog" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-700 px-2 py-2 rounded-lg w-full text-left hover:bg-gray-50">
                  Blog
                </Link>

                <button onClick={handleContactClick} className="text-gray-700 px-2 py-2 rounded-lg w-full text-left hover:bg-gray-50">
                  Contact
                </button>

                <Button
                  onClick={() => setIsQuoteFormOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white w-full py-2 rounded-lg"
                >
                  Get Quote
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>

      <QuoteForm isOpen={isQuoteFormOpen} onClose={() => setIsQuoteFormOpen(false)} />
    </>
  );
};

export default Header;
